/**
 * OtpInput — 6-cell one-time-password input.
 *
 * Built on top of `react-native-confirmation-code-field` which provides:
 *  • `textContentType="oneTimeCode"` → iOS auto-fill from email / Messages
 *  • `autoComplete="one-time-code"` → Android auto-fill
 *  • Focus-aware cell rendering with accessible hidden input
 *
 * Usage:
 *   <OtpInput value={code} onChangeText={setCode} />
 */

import React, { memo } from "react";
import { Platform, View } from "react-native";
import {
  CodeField,
  Cursor,
  useBlurOnFulfill,
  useClearByFocusCell,
  type RenderCellOptions,
} from "react-native-confirmation-code-field";
import { AppText } from "@/components/ui/AppText";
import { cn } from "@/lib/utils";

const CELL_COUNT = 6;

export interface OtpInputProps {
  /** Current 6-character value */
  value: string;
  /** Called whenever the value changes */
  onChangeText: (value: string) => void;
  /** Marks all cells red (e.g. after a failed verify attempt). */
  hasError?: boolean;
  /** Disables editing */
  editable?: boolean;
  /** Forwarded to the hidden TextInput for automation / testing */
  testID?: string;
}

export const OtpInput = memo(function OtpInput({
  value,
  onChangeText,
  hasError = false,
  editable = true,
  testID = "otp-input",
}: OtpInputProps) {
  const ref = useBlurOnFulfill({ value, cellCount: CELL_COUNT });
  const [clearProps, getCellOnLayoutHandler] = useClearByFocusCell({
    value,
    setValue: onChangeText,
  });

  function renderCell({ symbol, index, isFocused }: RenderCellOptions) {
    const isEmpty = symbol === "";
    const cellBorderClass = hasError
      ? "border-error"
      : isFocused
        ? "border-blue-50"
        : "border-stroke-50";

    const cellBgClass = !editable ? "bg-stroke-20" : "bg-white";

    return (
      <View
        key={index}
        onLayout={getCellOnLayoutHandler(index)}
        testID={`otp-cell-${index}`}
        className={cn(
          "flex-1 h-14 items-center justify-center rounded-md border",
          cellBorderClass,
          cellBgClass
        )}
        accessibilityRole="text"
        accessibilityLabel={`Digit ${index + 1}${symbol ? `: ${symbol}` : ""}`}
      >
        {isFocused && isEmpty ? (
          <AppText weight="semibold" className="text-2xl text-center text-dark-text-50">
            <Cursor />
          </AppText>
        ) : (
          <AppText
            weight="semibold"
            className={cn("text-2xl text-center", hasError ? "text-error" : "text-dark-text-50")}
          >
            {symbol}
          </AppText>
        )}
      </View>
    );
  }

  return (
    <CodeField
      ref={ref}
      {...clearProps}
      value={value}
      onChangeText={onChangeText}
      cellCount={CELL_COUNT}
      keyboardType="number-pad"
      textContentType="oneTimeCode"
      autoComplete={Platform.OS === "android" ? ("one-time-code" as const) : "off"}
      editable={editable}
      testID={testID}
      rootStyle={{ gap: 8 }}
      renderCell={renderCell}
    />
  );
});
